import BlogLayout from '@/pages/blog/components/BlogLayout';
import Breadcrumbs from '@/pages/blog/components/Breadcrumbs';
import TableOfContents from '@/pages/blog/components/TableOfContents';
import AuthorBio from '@/pages/blog/components/AuthorBio';
import RelatedArticles from '@/pages/blog/components/RelatedArticles';

const tocItems = [
  { id: 'intro', label: 'Life After Knee Replacement: The Real Goal' },
  { id: 'phases', label: 'Recovery Phases: What to Expect' },
  { id: 'early-exercises', label: 'Early Recovery Exercises (Weeks 1-6)' },
  { id: 'mid-exercises', label: 'Mid-Recovery Exercises (Weeks 6-12)' },
  { id: 'strength-phase', label: 'Strength Building Phase (Month 3+)' },
  { id: 'exercises-avoid', label: 'Exercises to Avoid After Knee Replacement' },
  { id: 'long-term', label: 'Long-Term Fitness After Knee Replacement' },
  { id: 'mason-approach', label: "Why Joint-Safe Training Matters" },
  { id: 'faq', label: 'Frequently Asked Questions' },
];

const relatedArticles = [
  { slug: 'knee-friendly-exercises-for-seniors', title: '10 Knee-Friendly Exercises for Men Over 60', category: 'Health & Recovery' },
  { slug: 'exercise-with-arthritis-over-60', title: 'Working Out with Arthritis Over 60: What You Can Still Do', category: 'Health Conditions' },
  { slug: 'joint-pain-exercises-for-seniors', title: 'Exercise WITH Joint Pain: Safe Movements for Men Over 60', category: 'Health & Recovery' },
];

const schema = JSON.stringify([
  {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Exercises After Knee Replacement: Getting Strong Again Over 60',
    author: { '@type': 'Person', name: 'Mason Hale', url: 'https://masonhalefit.com' },
    publisher: { '@type': 'Organization', name: 'Mason Hale Fitness', url: 'https://masonhalefit.com' },
    datePublished: '2024-03-01',
    dateModified: '2024-04-06',
    description: 'A complete guide to exercises after knee replacement for men over 60. Phase-by-phase recovery plan to rebuild strength and get back to full activity.',
    mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://masonhalefit.com/blog/exercises-after-knee-replacement-over-60' },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'When can I start exercising after knee replacement?',
        acceptedAnswer: { '@type': 'Answer', text: 'Most patients begin physical therapy within 24 hours of surgery. Walking with assistance starts immediately. The timeline for returning to independent exercise varies, but most men can begin a structured strength program 3-6 months post-surgery with their surgeon\'s clearance.' },
      },
      {
        '@type': 'Question',
        name: 'Can I lift weights after knee replacement?',
        acceptedAnswer: { '@type': 'Answer', text: 'Yes, with appropriate modifications. Upper body and core exercises can often begin within weeks of surgery. Lower body resistance training is reintroduced gradually, typically starting around 6-12 weeks post-op under physical therapist guidance.' },
      },
      {
        '@type': 'Question',
        name: 'What exercises should I avoid after knee replacement?',
        acceptedAnswer: { '@type': 'Answer', text: 'High-impact activities (running, jumping), deep squats beyond 90 degrees, kneeling directly on the replaced knee, and contact sports are generally avoided long-term. Your surgeon will provide specific restrictions based on your implant type.' },
      },
      {
        '@type': 'Question',
        name: 'How long does it take to fully recover from knee replacement?',
        acceptedAnswer: { '@type': 'Answer', text: 'Most men return to normal daily activities within 6-12 weeks. Full recovery — including return to recreational activities and strength training — typically takes 3-6 months. Some men continue to see improvement for up to a year post-surgery.' },
      },
    ],
  },
]);

export default function ExercisesAfterKneeReplacementPage() {
  return (
    <BlogLayout
      title="Exercises After Knee Replacement: Getting Strong Again Over 60"
      description="Phase-by-phase exercise guide after knee replacement for men over 60. Rebuild strength safely and get back to full activity with this recovery plan."
      keywords="exercises after knee replacement over 60, knee replacement recovery exercises, getting strong after knee replacement, knee replacement fitness"
      canonicalPath="/blog/exercises-after-knee-replacement-over-60"
      datePublished="2024-03-01"
      dateModified="2024-04-06"
      schemaMarkup={schema}
    >
      <Breadcrumbs title="Exercises After Knee Replacement: Getting Strong Again Over 60" />

      <div className="mb-6">
        <span className="text-[#c8a96e] text-xs font-bold tracking-[0.2em] uppercase">Health &amp; Recovery</span>
      </div>

      <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4 mb-8">
        <p className="text-yellow-300 text-sm font-bold mb-1">Medical Disclaimer</p>
        <p className="text-yellow-200/70 text-xs leading-relaxed">This article is for informational purposes only. Always follow your surgeon's and physical therapist's specific guidelines after knee replacement surgery. Individual recovery timelines vary significantly.</p>
      </div>

      <h1 className="text-white text-3xl sm:text-4xl font-black leading-tight mb-4">
        Exercises After Knee Replacement: Getting Strong Again Over 60
      </h1>

      <p className="text-gray-400 text-sm mb-8">
        By <strong className="text-white">Mason Hale</strong> &nbsp;·&nbsp; March 1, 2024 &nbsp;·&nbsp; 11 min read
      </p>

      <TableOfContents items={tocItems} />

      <section id="intro" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Life After Knee Replacement: The Real Goal</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          Knee replacement surgery is one of the most successful orthopedic procedures performed today. Over 700,000 Americans have the surgery each year, and the vast majority report significant pain reduction and improved quality of life.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          But the surgery is only half the equation. The other half — the part that determines how well you actually function long-term — is what you do in the months and years after.
        </p>
        <p className="text-gray-300 leading-relaxed">
          The goal isn't just to walk without pain. The goal is to build enough strength and fitness that your new knee lasts decades and you can live an active, independent life. This guide shows you how to get there.
        </p>
      </section>

      <section id="phases" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Recovery Phases: What to Expect</h2>
        <div className="space-y-3 mb-4">
          {[
            { phase: 'Phase 1', period: 'Days 1-14', title: 'Acute Recovery', desc: 'Hospital and early home recovery. Focus on pain management, swelling reduction, and basic mobility. Walking with assistance begins immediately.' },
            { phase: 'Phase 2', period: 'Weeks 2-6', title: 'Early Rehab', desc: 'Physical therapy begins. Range of motion exercises, gentle strengthening, and progressive walking. Most men use a walker or cane.' },
            { phase: 'Phase 3', period: 'Weeks 6-12', title: 'Active Rehab', desc: 'Increasing strength and mobility. Stationary cycling often begins. Walking without assistive devices for most men.' },
            { phase: 'Phase 4', period: 'Months 3-6', title: 'Strength Building', desc: 'Return to more structured exercise. Light resistance training reintroduced. Most daily activities fully restored.' },
            { phase: 'Phase 5', period: 'Month 6+', title: 'Long-Term Fitness', desc: 'Full return to recreational activities with surgeon clearance. Ongoing strength training to protect the implant long-term.' },
          ].map((item) => (
            <div key={item.phase} className="flex items-start gap-4 bg-[#111111] border border-white/10 rounded-lg p-4">
              <div className="flex-shrink-0 text-center min-w-[90px]">
                <span className="text-[#c8a96e] text-xs font-bold block">{item.period}</span>
                <span className="text-white text-sm font-black">{item.title}</span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="early-exercises" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Early Recovery Exercises (Weeks 1-6)</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          These exercises are typically prescribed by your physical therapist in the early recovery phase. Always follow your PT's specific guidance:
        </p>
        <ul className="space-y-3 mb-4">
          {[
            { name: 'Ankle pumps', desc: 'Flex and point your foot repeatedly. Improves circulation and reduces swelling. Do 10-20 reps every hour.' },
            { name: 'Quad sets', desc: 'Tighten your thigh muscle while lying flat. Hold 5 seconds, release. Reactivates the quadriceps after surgery.' },
            { name: 'Straight leg raises', desc: 'Lying flat, tighten quad and raise leg to 45 degrees. Builds quad strength without knee joint stress.' },
            { name: 'Heel slides', desc: 'Lying flat, slowly slide your heel toward your buttocks. Improves knee flexion range of motion.' },
            { name: 'Short arc quads', desc: 'Lying with a rolled towel under the knee, straighten the leg fully. Builds terminal knee extension strength.' },
            { name: 'Walking', desc: 'Progressive walking with assistive device as prescribed. Distance increases gradually each day.' },
          ].map((item) => (
            <li key={item.name} className="flex items-start gap-3 bg-[#111111] border border-white/10 rounded-lg p-4">
              <span className="text-[#c8a96e] mt-1 flex-shrink-0"><i className="ri-arrow-right-s-line" /></span>
              <div>
                <strong className="text-white">{item.name}:</strong>
                <span className="text-gray-400 text-sm ml-1">{item.desc}</span>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section id="mid-exercises" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Mid-Recovery Exercises (Weeks 6-12)</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          As healing progresses and your PT clears you, these exercises are typically introduced:
        </p>
        <ul className="space-y-2 mb-4">
          {[
            'Stationary cycling — excellent for range of motion and cardiovascular fitness with minimal joint stress',
            'Mini squats (0-45 degrees) — partial range squats to rebuild quad strength',
            'Step-ups on low step — functional strength for stairs',
            'Standing hip abduction — hip strength to support knee alignment',
            'Calf raises — lower leg strength and balance',
            'Water walking or pool exercises — buoyancy reduces joint load significantly',
          ].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <span className="text-[#c8a96e] mt-1 flex-shrink-0"><i className="ri-checkbox-circle-line" /></span>
              <span className="text-gray-300 text-sm">{item}</span>
            </li>
          ))}
        </ul>
      </section>

      <section id="strength-phase" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Strength Building Phase (Month 3+)</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          With surgeon clearance, a more structured strength program can begin. The <a href="https://www.mayoclinic.org/tests-procedures/knee-replacement/in-depth/knee-replacement/art-20048718" target="_blank" rel="noopener noreferrer" className="text-[#c8a96e] hover:text-[#d9bc82] underline">Mayo Clinic</a> emphasizes that ongoing strength training is critical for long-term implant success and function.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {[
            { exercise: 'Leg press (limited range)', desc: 'Builds quad and glute strength with controlled range of motion. Start light.' },
            { exercise: 'Dumbbell squats (partial)', desc: 'Functional strength. Keep depth comfortable — no forcing range.' },
            { exercise: 'Romanian deadlifts', desc: 'Hamstring and glute strength. Minimal knee stress.' },
            { exercise: 'Upper body training', desc: 'Chest, back, shoulders, arms — full upper body program can begin earlier.' },
            { exercise: 'Core exercises', desc: 'Planks, bird dogs, dead bugs — core stability supports knee alignment.' },
            { exercise: 'Balance training', desc: 'Single-leg stands, balance board — critical for long-term knee health.' },
          ].map((item) => (
            <div key={item.exercise} className="bg-[#111111] border border-white/10 rounded-lg p-4">
              <p className="text-white text-sm font-bold mb-1">{item.exercise}</p>
              <p className="text-gray-400 text-xs leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
        <p className="text-gray-300 leading-relaxed">
          Once you're cleared for independent strength training, a program like the <a href="https://masonhalefit.com" className="text-[#c8a96e] hover:text-[#d9bc82] underline font-semibold">Over-60 Strength Blueprint</a> — built around joint-safe movements — is an excellent long-term option. Always share any new program with your surgeon first.
        </p>
      </section>

      <section id="exercises-avoid" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Exercises to Avoid After Knee Replacement</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          Most surgeons recommend avoiding these activities long-term to protect the implant:
        </p>
        <ul className="space-y-2 mb-4">
          {[
            'Running and jogging — high impact accelerates implant wear',
            'Jumping and plyometric exercises',
            'Deep squats beyond 90 degrees (surgeon-dependent)',
            'Kneeling directly on the replaced knee',
            'Contact sports',
            'High-impact aerobics classes',
          ].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <span className="text-red-400 mt-1 flex-shrink-0"><i className="ri-close-circle-line" /></span>
              <span className="text-gray-300 text-sm">{item}</span>
            </li>
          ))}
        </ul>
        <p className="text-gray-300 leading-relaxed">
          These restrictions vary by surgeon and implant type. Always follow your specific surgeon's guidelines.
        </p>
      </section>

      <section id="long-term" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Long-Term Fitness After Knee Replacement</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          The <a href="https://www.nia.nih.gov/health/exercise-physical-activity" target="_blank" rel="noopener noreferrer" className="text-[#c8a96e] hover:text-[#d9bc82] underline">National Institute on Aging</a> recommends that older adults maintain regular strength training and aerobic exercise indefinitely. After knee replacement, this is even more important — strong muscles protect the implant and extend its lifespan.
        </p>
        <p className="text-gray-300 leading-relaxed">
          Men who maintain consistent strength training after knee replacement report better function, less pain, and greater satisfaction with their surgery outcomes compared to those who stop exercising after initial recovery.
        </p>
      </section>

      <section id="mason-approach" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Why Joint-Safe Training Matters</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          Whether you've had a knee replacement or are trying to avoid one, joint-safe training is the foundation of sustainable fitness after 60. I built the <a href="https://masonhalefit.com" className="text-[#c8a96e] hover:text-[#d9bc82] underline font-semibold">Over-60 Strength Blueprint</a> around this principle after experiencing my own joint issues at 60.
        </p>
        <p className="text-gray-300 leading-relaxed">
          Every exercise in the program is chosen specifically to build strength while protecting aging and recovering joints. It's the approach that helped me lose 55 lbs and get off 3 medications — and it's designed to work for men with the exact challenges you're facing.
        </p>
      </section>

      <section id="faq" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-6">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {[
            { q: 'When can I start exercising after knee replacement?', a: 'Most patients begin physical therapy within 24 hours of surgery. The timeline for returning to independent exercise varies, but most men can begin a structured strength program 3-6 months post-surgery with their surgeon\'s clearance.' },
            { q: 'Can I lift weights after knee replacement?', a: 'Yes, with appropriate modifications. Upper body and core exercises can often begin within weeks of surgery. Lower body resistance training is reintroduced gradually, typically starting around 6-12 weeks post-op.' },
            { q: 'What exercises should I avoid after knee replacement?', a: 'High-impact activities (running, jumping), deep squats beyond 90 degrees, kneeling directly on the replaced knee, and contact sports are generally avoided long-term.' },
            { q: 'How long does it take to fully recover from knee replacement?', a: 'Most men return to normal daily activities within 6-12 weeks. Full recovery typically takes 3-6 months. Some men continue to see improvement for up to a year post-surgery.' },
          ].map((item) => (
            <div key={item.q} className="bg-[#111111] border border-white/10 rounded-lg p-5">
              <p className="text-white font-bold mb-2">{item.q}</p>
              <p className="text-gray-400 text-sm leading-relaxed">{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="bg-[#c8a96e]/10 border border-[#c8a96e]/30 rounded-lg p-6 mb-10 text-center">
        <p className="text-[#c8a96e] font-black text-lg mb-2">Ready to Build Real Strength After Recovery?</p>
        <p className="text-gray-300 text-sm mb-4">The Over-60 Strength Blueprint is built around joint-safe movements perfect for men in long-term recovery. Always get your surgeon's clearance before starting any new program.</p>
        <a href="https://masonhalefit.com" className="inline-block bg-[#c8a96e] text-black font-black text-sm tracking-[0.15em] uppercase px-8 py-3 rounded-sm hover:bg-[#d9bc82] transition-all cursor-pointer whitespace-nowrap">
          Get The Blueprint — $19.99
        </a>
      </div>

      <AuthorBio />
      <RelatedArticles articles={relatedArticles} />
    </BlogLayout>
  );
}
